import React, { useEffect, useState, useRef } from "react";
import { X, Download } from "lucide-react";
import { API, resolveImage } from "../products";
import { useLang } from "../i18n";
import { track } from "../analytics";

// Homepage promotional popup. Shows once per browsing session; a new/relaunched
// banner (different id) will show again in the same session. Picks the image
// variant matching the visitor's language, falling back to the other.
export default function SitePopup() {
  const { lang } = useLang();
  const [popup, setPopup] = useState(null);
  const [open, setOpen] = useState(false);
  const openedAt = useRef(0);
  const viewedRef = useRef(false);

  useEffect(() => {
    let active = true;
    fetch(`${API}/site/popup`)
      .then((r) => r.json())
      .then((data) => {
        if (!active || !data?.enabled) return;
        if (!data.image_en && !data.image_es) return;
        const dismissed = sessionStorage.getItem(`tcs_popup_${data.id}`);
        setPopup(data);
        if (!dismissed) setOpen(true);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Track an impression once the popup is actually shown.
  useEffect(() => {
    if (open && popup && !viewedRef.current) {
      viewedRef.current = true;
      openedAt.current = Date.now();
      track("popup_view", { label: `${popup.id}|${lang}` });
    }
  }, [open, popup, lang]);

  const close = (method) => {
    if (popup) {
      sessionStorage.setItem(`tcs_popup_${popup.id}`, "1");
      const secs = openedAt.current ? Math.round((Date.now() - openedAt.current) / 1000) : 0;
      track("popup_close", { label: `${popup.id}|${lang}|${method}`, value: secs });
    }
    setOpen(false);
  };

  if (!open || !popup) return null;

  const imageUrl =
    lang === "es"
      ? popup.image_es || popup.image_en
      : popup.image_en || popup.image_es;
  const src = resolveImage(imageUrl);

  const download = async () => {
    track("popup_download", { label: `${popup.id}|${lang}` });
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = (blob.type && blob.type.split("/")[1]) || "png";
      a.download = `tele-comp-announcement.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.open(src, "_blank");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={() => close("overlay")}
      data-testid="site-popup-overlay"
    >
      <div
        className="relative w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="site-popup"
      >
        <button
          onClick={() => close("x")}
          aria-label="Close"
          data-testid="site-popup-close"
          className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5 text-slate-700" />
        </button>
        <img
          src={src}
          alt="Announcement"
          className="w-full h-auto max-h-[80vh] object-contain rounded-2xl shadow-2xl bg-white"
          data-testid="site-popup-image"
        />
        <div className="mt-3 flex justify-center">
          <button
            onClick={download}
            data-testid="site-popup-download"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/95 hover:bg-white text-slate-800 font-bold text-sm shadow-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            {lang === "es" ? "Descargar" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}
